import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import Global from '../../../store/global-selectors';
import { usePackingSlipsCustomizedFeatureGate } from '../../../feature-gates/use-packing-slips-customized-feature-gate';
import { useBTEmailExperienceNew } from '../../../hooks/use-seller-setting';

import { pages } from '../nav-items';
import { MessageDescriptorPropType } from '../../../prop-types/intl';
import { useAutomationRulesFeatureGate } from '../../../feature-gates/use-automation-rules-feature-gate';
import { LockIconWithTooltip } from '../../../feature-gates/lock-icon-with-tooltip';

import messages from './messages';
import commonStyles from '../settings-page.module.less';
import style from './style.module.less';

// pageKey from popular/index.js

const renderLink = (disabled, linkTo, linkTitle, isFeatureGated) => {
  if (isFeatureGated) {
    return <LockIconWithTooltip isClickable messageDescriptor={linkTitle} />;
  }
  if (disabled) {
    return (
      <Tooltip>
        <div className={style['disabled-text']}>
          <FormattedMessage {...linkTitle} />
        </div>
        <TooltipContent>
          <FormattedMessage {...messages.contactAdmin} />
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Link to={linkTo}>
      <Text textType={TextType.Paragraph} value={linkTitle} />
    </Link>
  );
};

const MostPopularSettingSection = ({ icon, title, subsections }) => {
  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasCustomEmailTemplates = !useBTEmailExperienceNew();
  const { basicPackingSlipsCustomizedFeature: hasCustomPackingSlipsFeature } = usePackingSlipsCustomizedFeatureGate();
  const { isUnlimitedAutomationRulesEnabled, isLimitedAutomationRulesEnabled } = useAutomationRulesFeatureGate();
  const featureMap = {
    hasCustomEmailTemplates,
    hasCustomPackingSlipsFeature,
    enableAutomationRules: isUnlimitedAutomationRulesEnabled || isLimitedAutomationRulesEnabled,
  };
  return (
    <div className={style.section}>
      <div className={style['left-side']}>{icon}</div>
      <div>
        <FlexRow className={style['add-ons-header-row']}>
          <Text className={commonStyles['header-level-3']} textType={TextType.H3} value={title} />
        </FlexRow>
        {subsections.map(({ pageKey, linkTitle, blurb, customLink }) => {
          // We're infering that if the consumer has passed a `customLink` property,
          //  the section here is not going to navigate within settings,
          //  and thus doesn't have any coupling to the settings pages implementation.
          const linkTo = !customLink && `/${pages.settings.location}/${pages[pageKey].location}`;
          const disabled = !customLink && !pages[pageKey].getIsVisible(userRoles, featureMap);
          const isFeatureGated = pages[pageKey].getIsFeatureGated(featureMap);
          return (
            <div key={pageKey} className={style.subsection}>
              {customLink || renderLink(disabled, linkTo, linkTitle, isFeatureGated)}
              {blurb ? <Text textType={TextType.Paragraph} value={blurb} /> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

MostPopularSettingSection.propTypes = {
  icon: PropTypes.node.isRequired,
  title: MessageDescriptorPropType.isRequired,
  subsections: PropTypes.arrayOf(
    PropTypes.shape({
      pageKey: PropTypes.oneOf(Object.keys(pages)),
      linkTitle: MessageDescriptorPropType.isRequired,
      blurb: MessageDescriptorPropType,
      linkTo: PropTypes.string,
    }),
  ).isRequired,
};

export default MostPopularSettingSection;
